import { htmlToText, containsHtmlTags } from "common";
import { describeMailboxItem, logBodyApiCall } from "./outlookLoadDebug";

const BODY_READ_RETRY_DELAYS_MS = [100, 250, 500, 1000];

const isRetryableOfficeBodyError = (error) => {
  const code = error?.code ?? error?.officeError?.code;
  if (code === 5001) {
    return true;
  }
  const message = error?.message || error?.officeError?.message || "";
  return message.includes("An internal error has occurred");
};

const getEmailBodyOnce = (item, bodyType, caller, attempt = 0) => {
  const trace = logBodyApiCall("start", caller, {
    api: "body.getAsync",
    bodyType,
    attempt,
    item: describeMailboxItem(item),
  });

  return new Promise((resolve, reject) => {
    item.body.getAsync(bodyType, (result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        logBodyApiCall("OK", caller, {
          ...trace,
          api: "body.getAsync",
          bodyType,
          attempt,
          valueLength: result.value?.length ?? 0,
        });
        resolve(result);
      } else {
        const error = new Error(result.error.message);
        error.code = result.error?.code;
        error.officeError = result.error;
        logBodyApiCall("FAIL", caller, {
          ...trace,
          api: "body.getAsync",
          bodyType,
          attempt,
          error,
          officeError: result.error,
        });
        reject(error);
      }
    });
  });
};

export const getEmailBody = async (item, bodyType, caller = "unknown", options = {}) => {
  const maxAttempts = options.maxAttempts ?? BODY_READ_RETRY_DELAYS_MS.length + 1;
  let lastError;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    if (attempt > 0) {
      const waitMs = BODY_READ_RETRY_DELAYS_MS[attempt - 1];
      console.log(`[outlookUtils] getEmailBody retry ${attempt}/${maxAttempts - 1} after ${waitMs}ms`, {
        caller,
        item: describeMailboxItem(item),
      });
      await new Promise((resolve) => setTimeout(resolve, waitMs));
    }

    try {
      return await getEmailBodyOnce(item, bodyType, caller, attempt);
    } catch (error) {
      lastError = error;
      if (!isRetryableOfficeBodyError(error) || attempt === maxAttempts - 1) {
        throw error;
      }
    }
  }

  throw lastError;
};

export const getRecepients = async (item) => {
  return new Promise((resolve, reject) => {
    item.to.getAsync((result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(result);
      } else {
        reject(new Error(result.error.message));
      }
    });
  });
};
export const getSender = async (item) => {
  return new Promise((resolve, reject) => {
    item.from.getAsync((result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(result);
      } else {
        reject(new Error(result.error.message));
      }
    });
  });
};
export const getCC = async (item) => {
  return new Promise((resolve, reject) => {
    item.cc.getAsync((result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(result);
      } else {
        reject(new Error(result.error.message));
      }
    });
  });
};

export const getSubject = async (item) => {
  return new Promise((resolve, reject) => {
    item.subject.getAsync((result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(result);
      } else {
        reject(new Error(result.error.message));
      }
    });
  });
};


function containsComment(str) {
  const comment = "<!-- Converted from text/plain format -->";
  return str.includes(comment);
}

export async function getBodyType(item, caller = "unknown") {
  const trace = logBodyApiCall("start", caller, {
    api: "body.getAsync",
    bodyType: "html",
    item: describeMailboxItem(item),
  });

  return new Promise((resolve, reject) => {
    item.body.getAsync("html", (result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        const coercionType = containsComment(result.value)
          ? Office.CoercionType.Text
          : Office.CoercionType.Html;
        logBodyApiCall("OK", caller, {
          ...trace,
          api: "body.getAsync",
          bodyType: "html",
          coercionType,
          valueLength: result.value?.length ?? 0,
        });
        resolve(coercionType);
      } else {
        logBodyApiCall("FAIL", caller, {
          ...trace,
          api: "body.getAsync",
          bodyType: "html",
          error: result.error,
          officeError: result.error,
        });
        reject(result.error);
      }
    });
  });
}
export const setItemBody = async (item, content) => {
  const insertWithCoercion = (bodyContent, coercionType) =>
    new Promise((resolve, reject) => {
      try {
        item.body.setSelectedDataAsync(bodyContent, { coercionType }, (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve();
          } else {
            reject(new Error(result.error?.message || "Failed to set item body"));
          }
        });
      } catch (error) {
        reject(error);
      }
    });

  if (containsHtmlTags(content)) {
    try {
      await insertWithCoercion(content, Office.CoercionType.Html);
      return;
    } catch (htmlError) {
      console.warn("HTML body insert failed, falling back to plain text:", htmlError);
    }
  }

  const coercionType = await getBodyType(item);
  const bodyContent = coercionType === Office.CoercionType.Html ? content : htmlToText(content);
  await insertWithCoercion(bodyContent, coercionType);
};

export const extractSubjectAndContent = (content) => {
  const lines = content.split("\n");
  let subjectLine = "";
  let remainingText = content;

  if (lines.length > 0 && lines[0].startsWith("Subject:")) {
    subjectLine = lines[0].replace("Subject:", "").trim();
    remainingText = lines.slice(1).join("\n");
  }

  return { subjectLine, remainingText };
};

export const getEmailSubject = async (item, caller = "unknown") => {
  const startedAt = performance.now();
  console.log("[outlookUtils] subject.getAsync START", { caller, item: describeMailboxItem(item) });

  return new Promise((resolve, reject) => {
    item.subject.getAsync((result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        console.log("[outlookUtils] subject.getAsync OK", {
          caller,
          elapsedMs: Math.round(performance.now() - startedAt),
          subject: result.value,
        });
        resolve(result.value);
      } else {
        const error = new Error(result.error.message);
        console.error("[outlookUtils] subject.getAsync FAIL", {
          caller,
          elapsedMs: Math.round(performance.now() - startedAt),
          error: error.message,
          officeError: result.error,
        });
        reject(error);
      }
    });
  });
};
